// game.js

import { attachEventListeners } from '../ui/eventListeners';
import { preloadAssets } from '../assets';
import { updateStats } from '../ui/stats';
import { snordeluxeAssets } from '../snordeluxeAssets';
import { showRewardsModal, saveRewards } from '../ui/rewards';
import { hideShopPage } from '../ui/shop';
import { getUser } from '../ui/views';
import { displayLeaderboard } from '../ui/leaderboard';
import { initializeChat, sendEventMessage, findActiveHostInChat } from '../ui/chat.js';
import { isMultiplayerDisabled } from '../ui/settings.js';
import { initMobileControlsToggle } from '../ui/mobile-controls.js';
import { startOnlinePresence } from '../services/PresenceService.js';
import { preloadThemeBackgrounds } from '../gameplay/battle-themes.js';
import {
    initializePeer,
    getMyPeerId,
    onDataReceived as onMultiplayerDataReceived,
    sendMessageToPeer,
    connectToPeer,
    broadcastMessage,
    startKeepAliveChecks,
    isPeerInitialized,
    testPeerReachability
} from '../services/MultiplayerService.js';

const scenes = {
    'Home': { image: 'assets/images/snordeluxe-background.webp', music: 'assets/music/lobby-music.mp3' },
    'Battleground': { image: 'assets/images/snordelux-battlefield.webp', music: 'assets/music/battle-music.mp3' },
};

const sounds = {};

// Function to initialize the game
function initializeGame() {
    console.log("Initializing game");
    preloadAssets(scenes);
    preloadSnordeluxeImages(); // Preload Snordeluxe images
    preloadThemeBackgrounds(); // Preload theme background images
    attachEventListeners();
    initializeUI();
    initializeChat(); // Initialize the chat
    initializeMultiplayerControls(); // Initialize multiplayer controls
    // Owner-only Ctrl+Shift+K → admin modal. Installed for all users
    // but the shortcut handler itself checks isOwner before opening.
    import('../ui/admin.js').then(({ installAdminShortcut }) => {
        installAdminShortcut(() => {
            const u = getUser();
            if (!u) return null;
            return { username: u.username, email: u.email || (typeof window !== 'undefined' && window.__currentUserEmail__) };
        });
    }).catch(err => console.warn('[game] admin shortcut install failed:', err));
}

// Function to initialize the UI
function initializeUI() {
    console.log("Initializing UI");
    const user = getUser();
    const loadingSpinner = document.querySelector('.loading-spinner');
    loadingSpinner.style.visibility = 'hidden';
    hideShopPage();
    const shopPage = document.getElementById('shopPage');
    shopPage.style.display = 'none';
    const rewardsModal = document.createElement('div');
    rewardsModal.className = 'rewards-modal';
    document.body.appendChild(rewardsModal);
    rewardsModal.style.display = 'none';
    console.log(user);
    
    // Initialize mobile controls toggle button
    initMobileControlsToggle();

    // Start online presence tracking
    if (user && user.username) {
        startOnlinePresence();
        console.log('Started online presence tracking for', user.username);
    }

    const lobbyMusic = document.getElementById('lobbyMusic');

    function playLobbyMusic() {
        lobbyMusic.play().catch(error => console.error("Error playing the music:", error));
    }

    function pauseLobbyMusic() {
        lobbyMusic.pause();
    }

    lobbyMusic.volume = 0.5;
    playLobbyMusic();
    displayLeaderboard(); // Fetch and display the leaderboard
}

function preloadSnordeluxeImages() {
    Object.values(snordeluxeAssets).forEach(snordeluxe => {
        const img = new Image();
        img.src = snordeluxe.image;
        console.log(`Preloading image: ${snordeluxe.image}`);
    });
}

function hideGameplayOverlay() {
    const overlay = document.querySelector('.gameplay-overlay');
    if (overlay) {
        overlay.style.display = 'none';
    }
}

// Function to initialize multiplayer controls and UI
function initializeMultiplayerControls() {
    const joinBattleButton = document.getElementById('joinBattleButton');
    const peerIdStatus = document.getElementById('peerIdStatus');
    const gameLobby = document.getElementById('gameLobby');

    if (!joinBattleButton || !peerIdStatus) {
        console.error('Multiplayer UI elements not found!');
        return;
    }

    joinBattleButton.addEventListener('click', async () => {
        if (joinBattleButton.disabled) return;

        peerIdStatus.textContent = 'Status: Initializing Peer...';
        joinBattleButton.disabled = true;
        joinBattleButton.textContent = 'Initializing...';

        try {
            const localPeerId = await initializePeer();
            peerIdStatus.innerHTML = `Status: Initialized! <br>Your Peer ID: ${localPeerId}`;
            joinBattleButton.textContent = 'Multiplayer Active';
            
            const user = getUser();
            if (user && user.username && localPeerId) {
                const announcementPayload = {
                    peerId: localPeerId,
                    username: user.username
                };
                sendEventMessage(user.username, announcementPayload, 'PEER_ANNOUNCEMENT');
                console.log('Peer announcement sent via chat server:', announcementPayload);
            } else {
                console.warn('Could not send peer announcement. User or PeerID missing.');
            }

            setupMultiplayerDataListener();
            startKeepAliveChecks();
            updateGameLobbyTestUI(localPeerId, gameLobby);

        } catch (error) {
            console.error('PeerJS initialization failed:', error);
            peerIdStatus.textContent = 'Status: Initialization Failed!';
            joinBattleButton.disabled = false;
            joinBattleButton.textContent = 'Retry Initialize';
        }
    });
}

// Function to handle incoming P2P data from MultiplayerService
function setupMultiplayerDataListener() {
    onMultiplayerDataReceived((senderPeerId, data) => {
        console.log(`P2P Data received in game.js from ${senderPeerId}:`, data);
        const gameLobby = document.getElementById('gameLobby');
        
        // Find or create a section for messages if it doesn't exist
        let directMessagesContainer = document.getElementById('directP2PMessages');
        if (!directMessagesContainer) {
            directMessagesContainer = document.createElement('div');
            directMessagesContainer.id = 'directP2PMessages';
            const title = document.createElement('h5');
            title.textContent = 'Received Direct P2P Messages:';
            title.style.marginTop = '10px';
            title.style.marginBottom = '5px';
            directMessagesContainer.appendChild(title);
            // Prepend it to gameLobby so it appears above any peer list that chat.js might render
            if(gameLobby.firstChild) {
                gameLobby.insertBefore(directMessagesContainer, gameLobby.firstChild);
            } else {
                gameLobby.appendChild(directMessagesContainer);
            }
        }

        const messageElement = document.createElement('p');
        messageElement.textContent = `Direct Msg from ${senderPeerId.substring(0,8)}...: ${JSON.stringify(data)}`;
        messageElement.style.fontSize = '10px';
        directMessagesContainer.appendChild(messageElement);
        directMessagesContainer.scrollTop = directMessagesContainer.scrollHeight;

        // Example: Automatically send a reply (can be useful for testing)
        // if (data.type === 'GREETING') {
        //     sendMessageToPeer(senderPeerId, { type: 'GREETING_ACK', message: `ACK: ${data.message}` });
        // }
    });
    console.log('Multiplayer data listener configured in game.js.');
}

// Renamed from addTestP2PButtons to reflect its current minimal purpose
function updateGameLobbyTestUI(myId, container) {
    // Clear only specific test elements if they exist, or manage selectively
    // For simplicity, if #gameLobby is primarily managed by chat.js now for peer list,
    // this function might just add a global broadcast button or be removed.

    let broadcastButton = document.getElementById('broadcastP2PButton');
    if (!broadcastButton) {
        broadcastButton = document.createElement('button');
        broadcastButton.id = 'broadcastP2PButton';
        broadcastButton.textContent = 'Broadcast P2P Greeting to All Connected';
        broadcastButton.style.marginTop = '15px';
        broadcastButton.style.display = 'block'; // Make it a bit more prominent
        
        const multiplayerControls = document.getElementById('multiplayer-controls');
        if (multiplayerControls) {
            multiplayerControls.appendChild(broadcastButton); // Append to the main multiplayer controls div
        }
    }
    
    broadcastButton.onclick = () => {
        broadcastMessage({ type: 'BROADCAST_GREETING', message: `General P2P hello from ${myId}!` });
        console.log('Sent P2P broadcast greeting.');
    };

    // The direct P2P message log is now managed by setupMultiplayerDataListener
    // The peer list and connection buttons are managed by chat.js's updateGameLobbyUI
}

export async function initiateMultiplayerBattle(selectedSnordeluxeName) {
    const user = getUser();
    if (!user) {
        alert("Please log in to start a multiplayer battle.");
        return;
    }

    // Check if multiplayer is disabled in settings
    if (isMultiplayerDisabled()) {
        console.log('[initiateMultiplayerBattle] Multiplayer is disabled in settings. Starting local battle instead.');
        
        // Import battle-manager module for local play using the same event system
        const BattleManager = await import(/* webpackPrefetch: true */ './battle-manager.js');
        
        // Start a single player battle using the event-driven system
        BattleManager.startSinglePlayerSession(selectedSnordeluxeName, user);
        return;
    }

    // Dynamic imports for service and manager
    const { initializePeer, getMyPeerId, isPeerInitialized, testPeerReachability } = await import('../services/MultiplayerService.js');
    const BattleManager = await import(/* webpackPrefetch: true */ './battle-manager.js'); // Import the module
    const ChatModule = await import('../ui/chat.js'); // For findActiveHostInChat

    const peerIdStatusElement = document.getElementById('peerIdStatus'); // For UI feedback

    if (!isPeerInitialized()) {
        if (peerIdStatusElement) peerIdStatusElement.textContent = 'Initializing Multiplayer Service...';
        try {
            await initializePeer();
            const myId = getMyPeerId();
            if (peerIdStatusElement) peerIdStatusElement.textContent = `Multiplayer Ready. Your ID: ${myId}`;
        } catch (error) {
            console.error("PeerJS initialization failed:", error);
            alert("Failed to initialize multiplayer service. Please try again.");
            if (peerIdStatusElement) peerIdStatusElement.textContent = 'Multiplayer Service Failed to Start.';
            return;
        }
    }

    const myPeerId = getMyPeerId();
    if (!myPeerId) {
        alert("Failed to get your Peer ID. Cannot start multiplayer.");
        if (peerIdStatusElement) peerIdStatusElement.textContent = 'Error: Could not get Peer ID.';
        return;
    }
    console.log(`[initiateMultiplayerBattle] My Peer ID: ${myPeerId}. Looking for active host...`);

    // Attempt to find an active host from chat messages
    const activeHost = ChatModule.findActiveHostInChat ? await ChatModule.findActiveHostInChat() : null;
    console.log(`[initiateMultiplayerBattle] Found activeHost:`, activeHost ? JSON.stringify(activeHost) : 'null');

    if (activeHost && activeHost.hostPeerId && activeHost.hostPeerId !== myPeerId) {
        // Skip reachability test for non-recent hosts (older than 30 seconds)
        if (!activeHost.isRecent) {
            console.log(`[initiateMultiplayerBattle] Host ${activeHost.hostPeerId} advertisement is ${Math.round(activeHost.ageMs/1000)}s old. Skipping reachability test and hosting new game.`);
            if (peerIdStatusElement) peerIdStatusElement.textContent = `${activeHost.hostUsername}\'s game appears to be inactive. Hosting new game...`;
            // Immediately fallback to hosting
            BattleManager.startMultiplayerHostSession(selectedSnordeluxeName, user);
            return;
        }
        
        console.log(`[initiateMultiplayerBattle] Testing reachability of recent host ${activeHost.hostPeerId}...`);
        if (peerIdStatusElement) peerIdStatusElement.textContent = `Checking if ${activeHost.hostUsername}\'s game is available...`;
        
        // Test if the host is reachable before attempting to join - use shorter timeout for better UX
        const isReachable = await testPeerReachability(activeHost.hostPeerId, 1500); // Reduced from 5000ms to 1500ms
        
        if (!isReachable) {
            console.log(`[initiateMultiplayerBattle] Host ${activeHost.hostPeerId} is not reachable. Falling back to hosting.`);
            if (peerIdStatusElement) peerIdStatusElement.textContent = `${activeHost.hostUsername}\'s game is no longer available. Hosting new game...`;
            // Fallback to hosting
            BattleManager.startMultiplayerHostSession(selectedSnordeluxeName, user);
            return;
        }
        
        console.log(`[initiateMultiplayerBattle] Host ${activeHost.hostPeerId} is reachable. Attempting to join...`);
        console.log(`[initiateMultiplayerBattle] Decision: JOINING host ${activeHost.hostPeerId}. Player Snordeluxe: ${selectedSnordeluxeName}`);
        if (peerIdStatusElement) peerIdStatusElement.textContent = `Found game by ${activeHost.hostUsername}. Attempting to join...`;
        
        const joinedSuccessfully = await BattleManager.joinMultiplayerClientSession(activeHost.hostPeerId, selectedSnordeluxeName, user);

        if (joinedSuccessfully) {
            console.log("[initiateMultiplayerBattle] Successfully initiated connection to host. Waiting for join confirmation.");
            if (peerIdStatusElement) peerIdStatusElement.textContent = `Connecting to ${activeHost.hostUsername}\'s game...`;
            // The rest of the joining process (receiving game state, etc.) is handled by P2P message handlers
        } else {
            console.log(`[initiateMultiplayerBattle] Failed to connect to host ${activeHost.hostPeerId}. Falling back to hosting a new game.`);
            if (peerIdStatusElement) peerIdStatusElement.textContent = `Could not join ${activeHost.hostUsername}. Hosting new game...`;
            // Fallback to hosting
            BattleManager.startMultiplayerHostSession(selectedSnordeluxeName, user);
        }
    } else {
        if (activeHost && activeHost.hostPeerId === myPeerId) {
            console.log(`[initiateMultiplayerBattle] Decision: ALREADY HOSTING (my Peer ID ${myPeerId} matches activeHost.hostPeerId ${activeHost.hostPeerId}).`);
            alert("You appear to be already hosting a game. If not, please wait a moment for chat to clear.");
            if (peerIdStatusElement) peerIdStatusElement.textContent = `You are already hosting. Your ID: ${myPeerId}`;
            return;
        }
        console.log(`[initiateMultiplayerBattle] Decision: HOSTING NEW GAME (my Peer ID ${myPeerId}). ActiveHost condition failed. ActiveHost was: ${activeHost ? JSON.stringify(activeHost) : 'null'}`);
        if (peerIdStatusElement) peerIdStatusElement.textContent = `Hosting new game... Your ID: ${myPeerId}`;
        BattleManager.startMultiplayerHostSession(selectedSnordeluxeName, user);
    }
}

// New entry points used by the lobby browser. The old
// `initiateMultiplayerBattle` decided host-vs-join silently inside one
// function; the lobby surfaces that decision to the user, so we expose
// the two paths directly.
async function ensurePeerReady() {
    const { initializePeer, getMyPeerId, isPeerInitialized } = await import('../services/MultiplayerService.js');
    if (!isPeerInitialized()) {
        try {
            await initializePeer();
        } catch (err) {
            alert("Multiplayer service couldn't start. Try again in a moment.");
            return null;
        }
    }
    const myId = getMyPeerId();
    if (!myId) {
        alert("Multiplayer service didn't return a peer ID. Try again.");
        return null;
    }
    return myId;
}

export async function playSingle(selectedSnordeluxeName) {
    const user = getUser();
    if (!user) { alert("Please log in first."); return; }
    // Game modes are a HOST-only feature for multiplayer matches —
    // don't auto-apply them to solo play, otherwise toggles a player
    // set up for an MP match leak into their next "Play vs AI" run
    // (e.g. Fat Mode silently turning itself on solo).
    const BattleManager = await import(/* webpackPrefetch: true */ './battle-manager.js');
    BattleManager.startSinglePlayerSession(selectedSnordeluxeName, user);
}

export async function becomeHost(selectedSnordeluxeName) {
    const user = getUser();
    if (!user) { alert("Please log in first."); return; }
    if (isMultiplayerDisabled()) {
        const BattleManager = await import(/* webpackPrefetch: true */ './battle-manager.js');
        BattleManager.startSinglePlayerSession(selectedSnordeluxeName, user);
        return;
    }
    const myId = await ensurePeerReady();
    if (!myId) return;
    // Lives-per-player + game-mode toggles are host-only choices; pull
    // them from the lobby browser's persisted settings so each new hosted
    // match honors whatever the host last picked.
    const { getHostLivesSetting, getHostGameModes } = await import('../ui/lobbyBrowser.js');
    const livesPerPlayer = getHostLivesSetting();
    const gameModes = getHostGameModes();
    const BattleManager = await import(/* webpackPrefetch: true */ './battle-manager.js');
    BattleManager.startMultiplayerHostSession(selectedSnordeluxeName, user, { livesPerPlayer, gameModes });
}

export async function joinHost(hostPeerId, selectedSnordeluxeName, hostUsername) {
    const user = getUser();
    if (!user) { alert("Please log in first."); return; }
    if (isMultiplayerDisabled()) {
        alert("Multiplayer is disabled in your settings.");
        return;
    }
    const myId = await ensurePeerReady();
    if (!myId) return;
    if (hostPeerId === myId) {
        alert("That's your own match — pick a different host.");
        return;
    }
    const { testPeerReachability } = await import('../services/MultiplayerService.js');
    const reachable = await testPeerReachability(hostPeerId, 1500);
    if (!reachable) {
        alert(`${hostUsername || 'That host'} is no longer reachable.`);
        return;
    }
    const BattleManager = await import(/* webpackPrefetch: true */ './battle-manager.js');
    BattleManager.joinMultiplayerClientSession(hostPeerId, selectedSnordeluxeName, user);
}

export { initializeGame, hideGameplayOverlay };